import { ResolvedPos } from 'prosemirror-model';
import { EditorState, NodeSelection, TextSelection, Transaction } from 'prosemirror-state';
import { liftTarget } from 'prosemirror-transform';
import { EditorView } from 'prosemirror-view';

import { ColumnsService } from './columns.service';

const QUICK_ENTER_MS = 500;

export class ColumnsKeysService {
  private lastEnterTime = 0;

  constructor(private columnsService: ColumnsService) {}

  public handleEnter(state: EditorState, dispatch: ((tr: Transaction) => void) | null): boolean {
    const { selection } = state;
    const { $from } = selection;
    const isEmpty = this.isEmptyParagraph($from);
    const inList = this.isInNodeType($from, 'list_item');
    const inQuote = this.isInNodeType($from, 'blockquote');

    if (!isEmpty) {
      return false;
    }

    const columnDepth = this.findColumnDepth($from);
    if (columnDepth === -1) return false; // not in a column

    if (inList || inQuote) {
      // Try to lift the empty list item or exit the blockquote
      const range = $from.blockRange();
      const target = range && liftTarget(range);

      if (target !== null && target !== undefined) {
        const tr = state.tr.lift(range!, target);
        if (dispatch) {
          dispatch(tr);
        }
        return true;
      }
      // If lifting failed, fall back to inserting a new paragraph
      return this.insertInsideColumn(state, dispatch!, $from);
    }

    if (selection instanceof NodeSelection && selection.node.isBlock) {
      if (!dispatch) return true;

      const pos = selection.$anchor.pos + selection.node.nodeSize;
      const paragraph = state.schema.nodes.paragraph.createAndFill();
      if (!paragraph) return false;

      let tr = state.tr.insert(pos, paragraph);
      tr = tr.setSelection(TextSelection.near(tr.doc.resolve(pos + 1)));
      tr = tr.scrollIntoView();
      dispatch(tr);
      return true;
    }

    const now = Date.now();
    const fastDouble = now - this.lastEnterTime < QUICK_ENTER_MS;
    this.lastEnterTime = now;

    const isLastColumn = this.isInLastColumn($from, columnDepth);
    const isLastPara = this.isLastParaInColumn($from, columnDepth);

    const isLastColumnBlock = isLastColumn && isLastPara;
    if (fastDouble && isEmpty && isLastColumnBlock) {
      if (dispatch && this.isSafeToLiftFromColumn($from)) {
        return this.tryLiftOrInsertOutside(state, dispatch, $from);
      }
      return true;
    }

    if (!dispatch) return true;
    return this.insertInsideColumn(state, dispatch, $from);
  }

  public handleBackspace(
    state: EditorState,
    dispatch: ((tr: Transaction) => void) | null,
    view?: EditorView,
  ): boolean {
    const { selection } = state;
    const { $from } = selection;

    if ($from.depth === 1 && $from.parent.isBlock) {
      const paraIndex = $from.index(0);
      if (paraIndex === 0) return false;

      const prevNode = state.doc.child(paraIndex - 1);
      if (prevNode?.type.name === 'columns') {
        const columnsPos = $from.before(1) - prevNode.nodeSize;
        const columns = prevNode;
        const lastIdx = columns.childCount - 1;
        const lastCol = columns.child(lastIdx);

        if (dispatch) {
          const lastColStart = this.columnsService.getColumnStart(columns, columnsPos, lastIdx);
          const posInLastPara = lastColStart + lastCol.nodeSize - 1;
          const tr = state.tr.setSelection(TextSelection.create(state.doc, posInLastPara));
          dispatch(tr);
        }
        return true;
      }
    }

    for (let depth = $from.depth; depth >= 0; depth--) {
      const node = $from.node(depth);
      if (node.type.name !== 'column') continue;

      const columnDepth = depth;
      const columnsDepth = columnDepth - 1;
      if (columnsDepth < 0) return false;

      const columnsNode = $from.node(columnsDepth);
      if (columnsNode.type.name !== 'columns' || columnsNode.childCount !== 2) return false;

      const columnIndex = $from.index(columnsDepth);
      if (columnIndex !== 1) return false;

      const columnNode = $from.node(columnDepth);
      const onlyChild = columnNode.firstChild;

      const isOnlyEmptyBlock =
        columnNode.childCount === 1 && onlyChild?.isBlock && onlyChild.content.size === 0;

      if (isOnlyEmptyBlock && dispatch && view) {
        this.columnsService.setColumns(1, state, view);
        return true;
      }
    }

    return false;
  }

  private isInNodeType($from: ResolvedPos, typeName: string): boolean {
    for (let i = $from.depth; i >= 0; i--) {
      if ($from.node(i).type.name === typeName) {
        return true;
      }
    }
    return false;
  }

  private findColumnDepth($from: ReturnType<EditorState['doc']['resolve']>): number {
    for (let i = $from.depth; i >= 0; i--) {
      if ($from.node(i).type.name === 'column') {
        return i;
      }
    }
    return -1;
  }

  private isInLastColumn(
    $from: ReturnType<EditorState['doc']['resolve']>,
    columnDepth: number,
  ): boolean {
    const columnsDepth = columnDepth - 1;
    if (columnsDepth < 0) return false;
    const columnsNode = $from.node(columnsDepth);
    if (columnsNode.type.name !== 'columns') return false;

    const columnIndex = $from.index(columnsDepth);
    return columnIndex === columnsNode.childCount - 1;
  }

  private isEmptyParagraph($from: ReturnType<EditorState['doc']['resolve']>): boolean {
    const paragraph = $from.node($from.depth);
    return paragraph.type.name === 'paragraph' && paragraph.content.size === 0;
  }

  private isLastParaInColumn(
    $from: ReturnType<EditorState['doc']['resolve']>,
    columnDepth: number,
  ): boolean {
    const paraIndex = $from.index(columnDepth);
    const column = $from.node(columnDepth);
    return paraIndex === column.childCount - 1;
  }

  private isSafeToLiftFromColumn($from: ResolvedPos): boolean {
    const columnDepth = this.findColumnDepth($from);
    if (columnDepth === -1) return false;

    if (!this.isInLastColumn($from, columnDepth)) return false;

    const column = $from.node(columnDepth);

    const isOnlyEmptyParagraph =
      column.childCount === 1 &&
      column.firstChild?.type.name === 'paragraph' &&
      column.firstChild.content.size === 0;

    // Don't lift if the column is only an empty paragraph
    return !isOnlyEmptyParagraph;
  }

  private tryLiftOrInsertOutside(
    state: EditorState,
    dispatch: (tr: Transaction) => void,
    $from: ResolvedPos,
  ): boolean {
    const paragraphType = state.schema.nodes.paragraph;
    if (!paragraphType) return false;

    const columnsDepth = this.findColumnDepth($from) - 1;
    const columnsNode = $from.node(columnsDepth);
    const columnsPos = $from.before(columnsDepth);
    let afterColumnsPos = columnsPos + columnsNode.nodeSize;
    if (afterColumnsPos > state.doc.content.size) {
      afterColumnsPos = state.doc.content.size;
    }
    const nextNode = state.doc.nodeAt(afterColumnsPos);
    let tr = state.tr;
    let insertPos = afterColumnsPos;

    if (nextNode?.type !== paragraphType) {
      const newParagraph = paragraphType.createAndFill();
      if (!newParagraph) return false;

      insertPos = Math.min(afterColumnsPos, tr.doc.content.size);
      tr = tr.insert(insertPos, newParagraph);
    }

    const selection =
      TextSelection.findFrom(tr.doc.resolve(insertPos), 1, true) ??
      TextSelection.findFrom(tr.doc.resolve(insertPos), -1, true) ??
      TextSelection.atEnd(tr.doc);

    tr = tr.setSelection(selection).scrollIntoView();
    dispatch(tr);
    return true;
  }

  private insertInsideColumn(
    state: EditorState,
    dispatch: (tr: Transaction) => void,
    $from: ReturnType<EditorState['doc']['resolve']>,
  ): boolean {
    const paragraph = $from.node($from.depth);
    const newParagraph = paragraph.type.create(paragraph.attrs);

    const paragraphPos = $from.before($from.depth);
    const afterPos = paragraphPos + paragraph.nodeSize;

    const tr1 = state.tr.insert(afterPos, newParagraph);
    dispatch(tr1);

    const newState = state.apply(tr1);
    const resolved = newState.doc.resolve(afterPos + 1);
    const tr2 = newState.tr.setSelection(TextSelection.near(resolved));
    dispatch(tr2);

    return true;
  }
}
